'use client';

import { useEffect, useState } from 'react';
import { Eye, FolderOpen, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { listarExpedientes, type Expediente } from '@/lib/expedientes';
import { labelSituacion, ROLES_CORRECTOR_ETAPA2 } from '@/lib/decisionFlujo';

// Estados en los que el contratista/tecnólogo puede editar la Etapa 2 (caso en sus manos).
const ESTADOS_EDITABLES = ['PENDIENTE_CONTRATISTA', 'DEVUELTA_ETAPA2'];
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

function investigadoPrincipal(e: Expediente): string {
  const inv = e.investigados ?? [];
  if (inv.length === 0) return '—';
  const det = inv.find((x) => x.tipo === 'DETERMINADO' && x.nombre) ?? inv[0];
  return det.nombre || (det.tipo === 'POR_ESTABLECER' ? 'Individualizar' : '—');
}

// Lee el id del usuario en sesión desde localStorage ('usuario').
function miId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('usuario');
    if (!raw) return null;
    const u = JSON.parse(raw) as { id?: string };
    return u?.id ?? null;
  } catch {
    return null;
  }
}

// Rol del usuario en sesión (para habilitar "Editar" en casos devueltos).
function miRol(): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = localStorage.getItem('usuario');
    return raw ? (JSON.parse(raw) as { rol?: string }).rol ?? '' : '';
  } catch {
    return '';
  }
}

export default function MisExpedientes({
  onAbrir, onEditarEtapa2,
}: { onAbrir?: (id: string) => void; onEditarEtapa2?: (id: string) => void }) {
  const puedeCorregir = ROLES_CORRECTOR_ETAPA2.includes(miRol());
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const id = miId();
    if (!id) {
      toast.error('No se pudo identificar al usuario en sesión.');
      setCargando(false);
      return;
    }
    // "Mis expedientes" = todo en lo que participé (asignados, radicados, propuestos,
    // con visto bueno o aprobados) — así conservo la trazabilidad tras aprobar.
    listarExpedientes({ participanteId: id, limit: 100 })
      .then((res) => setExpedientes(res.data))
      .catch((e) =>
        toast.error('No se pudieron cargar sus expedientes', {
          description: e instanceof Error ? e.message : undefined,
        }),
      )
      .finally(() => setCargando(false));
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-10 items-center justify-center rounded-lg bg-accent text-accent-foreground">
          <FolderOpen className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mis expedientes</h1>
          <p className="text-sm text-muted-foreground">
            Expedientes en los que ha intervenido (asignados, radicados, propuestos, con su visto bueno o aprobados).
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Radicado</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Etapa</TableHead>
                  <TableHead>Situación</TableHead>
                  <TableHead>Investigado principal</TableHead>
                  <TableHead>Prescripción</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expedientes.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {e.radicado}
                      {e.fecha_hechos_tentativa && (
                        <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-700 dark:text-amber-400" title="Fecha de los hechos tentativa — falta la real">Fecha tentativa</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge>{e.estado_actual}</Badge>
                    </TableCell>
                    <TableCell>{e.etapa_actual || '—'}</TableCell>
                    <TableCell>
                      {e.decision_estado
                        ? <Badge variant="secondary">{labelSituacion(e.decision_estado)}</Badge>
                        : '—'}
                    </TableCell>
                    <TableCell>{investigadoPrincipal(e)}</TableCell>
                    <TableCell>{fmtFecha(e.fecha_prescripcion)}</TableCell>
                    <TableCell>{fmtFecha(e.fecha_queja)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {/* El «Ver motivo» de la devolución vive ahora en «Decisión inicial». */}
                        {puedeCorregir && ESTADOS_EDITABLES.includes(e.decision_estado ?? '') && (
                          <Button size="sm" onClick={() => onEditarEtapa2?.(e.id)} title="Editar los datos de la Etapa 2">
                            <Pencil className="size-3.5" /> Editar
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => onAbrir?.(e.id)}>
                          <Eye className="size-3.5" /> Ver
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {expedientes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      No tiene expedientes asignados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
